import { RuleCondition } from "@/lib/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SubjectConditionSummary } from "./SubjectConditionSummary";
import { ScheduleConditionSummary } from "./ScheduleConditionSummary";
import { SenderMatchConditionSummary } from "./SenderMatchConditionSummary";
import { SenderTypeCondition } from "./SenderTypeCondition";
import { getDefaultScheduleValue } from "@/lib/schedule";

export type ConditionType = "subject_includes_words" | "subject_matches_patterns" | "schedule" | "sender_match_patterns" | "sender_type";

interface ConditionBuilderProps {
  condition: RuleCondition;
  onChange: (condition: RuleCondition) => void;
  /** Types already used elsewhere in the rule; rendered disabled in the Select. */
  disabledTypes?: ConditionType[];
}

const conditionTypes: { value: ConditionType; label: string }[] = [
  { value: "subject_includes_words", label: "Subject includes any of these words" },
  { value: "subject_matches_patterns", label: "Subject matches these text patterns" },
  { value: "schedule", label: "Email received during these times" },
  { value: "sender_match_patterns", label: "Sender matches one of these addresses" },
  { value: "sender_type", label: "Sender is internal / external" }
];

export function ConditionBuilder({ condition, onChange, disabledTypes = [] }: ConditionBuilderProps) {
  // Derived from props: handleTypeChange writes the new type straight back
  // through onChange, so a local copy could only ever go stale.
  const type = condition.type as ConditionType;

  const handleTypeChange = (value: ConditionType) => {
    // Set default value based on type
    let defaultValue: any;
    switch (value) {
      case "subject_includes_words":
      case "subject_matches_patterns":
      case "sender_match_patterns":
        defaultValue = [];
        break;
      case "schedule":
        defaultValue = getDefaultScheduleValue();
        break;
      case "sender_type":
        defaultValue = "external";
        break;
      default:
        defaultValue = "";
    }
    
    onChange({
      type: value,
      value: defaultValue
    });
  };

  const handleValueChange = (value: any) => {
    onChange({
      ...condition,
      value
    });
  };

  const renderConditionEditor = () => {
    switch (type) {
      case "subject_includes_words":
      case "subject_matches_patterns":
        return (
          <SubjectConditionSummary 
            type={type}
            value={condition.value || []}
            onChange={handleValueChange}
          />
        );
      case "schedule":
        return (
          <ScheduleConditionSummary
            value={condition.value}
            onChange={handleValueChange}
          />
        );
      case "sender_match_patterns":
        return (
          <SenderMatchConditionSummary
            value={condition.value || []}
            onChange={handleValueChange}
          />
        );
      case "sender_type":
        return (
          <SenderTypeCondition
            value={condition.value || "external"}
            onChange={handleValueChange}
          />
        );
      default:
        // A row whose type this build no longer knows — e.g. a retired
        // `time_range` that outlived its migration. Render it inertly rather
        // than stringifying an object into an Input.
        return (
          <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-md">
            Unsupported condition type
          </p>
        );
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium">Condition Type</label>
        <Select value={type} onValueChange={handleTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select condition type" />
          </SelectTrigger>
          <SelectContent>
            {conditionTypes.map((condType) => (
              <SelectItem
                key={condType.value}
                value={condType.value}
                disabled={disabledTypes.includes(condType.value)}
              >
                {condType.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <label className="text-sm font-medium">Condition Value</label>
        {renderConditionEditor()}
      </div>
    </div>
  );
}
